import { computed, effect, Injectable, signal } from '@angular/core';
import { environment } from './environment';
import { proxies } from 'proxies/proxies';

@Injectable({ providedIn: 'root' })
export class EnvService {
  envsList = environment.availableBaseurls;

  endpointsMap = this.envsList.reduce((acc, env) => {
    acc[env.value] = env.endpoint;
    return acc;
  }, {});

  customEnvironment = signal<string>(window.localStorage.getItem('_customEnvironment'));

  customBaseUrl = computed(() => {
    const _cEnv = this.customEnvironment();
    const _cEnvValue = this.endpointsMap[_cEnv];
    return _cEnvValue;
  });

  baseurl = computed(() => {
    if (this.isLocalHost()) {
      return this.customBaseUrl() || environment.baseurl;
    }
    return environment.baseurl;
  });

  isCustomEnvironment = computed(() => this.customBaseUrl() !== environment.baseurl);

  isCustomDev = computed(() => this.customEnvironment() === 'dev');
  isCustomRc = computed(() => this.customEnvironment() === 'rc');
  isCustomProd = computed(() => this.customEnvironment() === 'prod');

  localAndNotDev = computed(
    () =>
      !window.location.origin.includes('programmatic.fluidnext.com') &&
      this.baseurl().includes('programmatic.fluidnext.com') &&
      !this.baseurl().includes('api-dev'),
  );

  isLocalHost = computed(() => ['localhost', '127.0.0.1'].includes(window.location.hostname));

  isDevEnvironment = computed(() => !this.isLocalHost() && this.baseurl().includes('api-dev'));

  onCustomEnvironmentChange(event: string) {
    window.location.reload();
  }

  constructor() {
    effect(() => {
      window.localStorage.setItem('_customEnvironment', this.customEnvironment() ?? '');
    });
  }

  getProxiedUrl(url: string) {
    let baseurl = this.baseurl();
    // APPLY PROXIES
    // console.log('PROXIES', proxies);
    if (Array.isArray(proxies) && proxies.length > 0) {
      for (const proxy of proxies) {
        if (Array.isArray(proxy.routes)) {
          for (const route of proxy.routes) {
            if (url === route.path) {
              if (route.target) url = route.target;
              if (proxy.baseurl) baseurl = proxy.baseurl;
              break;
            }

            if (route.path.includes('*')) {
              const { regex, targetTemplate } = prepareWildcardRoute(route.path, route.target);

              if (regex.test(url)) {
                // console.log('PROXIES wildcard match:', url, route.path);

                if (targetTemplate) {
                  // Replaces wildcards based on position ($1, $2, etc.)
                  url = url.replace(regex, targetTemplate);
                }

                if (proxy.baseurl) {
                  baseurl = proxy.baseurl;
                }
                break;
              }
            }
          }
        }
      }
    }

    return `${baseurl}${url}`;
  }
}

/**
 * Returns a RegExp and a converted target string using standard regex replacement tokens ($1, $2, etc.)
 */
const prepareWildcardRoute = (path: string, target?: string): { regex: RegExp; targetTemplate?: string } => {
  let escapedPath = path.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  let targetTemplate = target;
  let groupCount = 0;

  // 1. Convert mid-path '*' to capture groups
  escapedPath = escapedPath.replace(/\*(?!$)/g, () => {
    groupCount++;
    return '([^/]+)';
  });

  // 2. Convert trailing '*' to capture group
  if (escapedPath.endsWith('*')) {
    groupCount++;
    escapedPath = escapedPath.slice(0, -1) + '(.*)';
  }

  // 3. Convert target wildcards to positional tokens or empty strings if out of bounds
  if (targetTemplate && targetTemplate.includes('*')) {
    let targetIndex = 1;
    targetTemplate = targetTemplate.replace(/\*/g, () => {
      if (targetIndex > groupCount) {
        return ''; // Drop the wildcard entirely if the source has no matching group
      }
      return `$${targetIndex++}`;
    });
  }

  return {
    regex: new RegExp(`^${escapedPath}$`),
    targetTemplate,
  };
};
